import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { CommerceService } from "@/services/commerce"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { internalCustomerException } from "../utils/customerQueryHelpers"

export const mutationUpdateCustomerCharacteristics = {
  async updateCustomerCharacteristics(parent, args, context, info) {
    const { customerId, customerKey, values } = args
    const commerceApi = CommerceService.api()
    const { user } = context

    if (!values) return null

    const customType = (await commerceApi.types().withKey({ key: "customerCustomTypeKey" }).get().execute())?.body

    const customer = await getCustomer({ id: customerId, key: customerKey })
    if (!customer) return null

    await Promise.all([
      checkPermissions({
        user,
        domainObject: DomainObjects.CUSTOMERS,
        operation: Operations.UPDATE,
      }),
      validateAccessRightsToCustomer({ user, customerNumber: customer.customerNumber }),
      internalCustomerException({ customerNumber: customer.customerNumber }),
    ])

    const hasCustomType = customer?.custom?.type?.id || false

    // adms have a different type to normal customer, target is wrong
    if (hasCustomType && customer.custom.type.id !== customType.id) return null

    let actions = []

    // first add the type where the values are going to be stored
    if (!hasCustomType) {
      actions = [
        {
          action: "setCustomType",
          type: { id: customType.id, typeId: "type" },
          fields: {
            numberOfEmployees: values.numberOfEmployees,
            businessVolume: values.businessVolume,
            customers: values.customers,
            memberships: values.memberships,
            activeCompetitors: values.activeCompetitors,
            foundingDate: values.foundingDate,
          },
        },
      ]
    } else {
      // go over each value and set actions
      Object.keys(values).forEach((valueKey) => {
        actions.push({
          action: "setCustomField",
          name: valueKey,
          value: values[valueKey],
        })
      })
    }

    actions.push(
      {
        action: "setCustomField",
        name: "modificationSource",
        value: "Commercetools",
      },
      {
        action: "setCustomField",
        name: "modifiedBy",
        value: user.employeeNumber,
      },
      {
        action: "setCustomField",
        name: "modifiedAt",
        value: new Date(),
      },
    )

    const payload = {
      version: customer.version,
      actions,
    }

    try {
      const updatedCustomer = (
        await commerceApi
          .customers()
          .withId({ ID: customer.id })
          .post({
            body: JSON.stringify(payload),
          })
          .execute()
      )?.body

      const { custom: customFields, defaultBillingAddressId, addresses } = updatedCustomer

      const customCustomerCharacteristics = {
        numberOfEmployees: customFields?.fields?.numberOfEmployees ?? null,
        businessVolume: customFields?.fields?.businessVolume ?? null,
        customers: customFields?.fields?.customers ?? null,
        memberships: customFields?.fields?.memberships ?? null,
        activeCompetitors: customFields?.fields?.activeCompetitors ?? null,
        foundingDate: customFields?.fields?.foundingDate ?? null,
      }

      // return object with updated customer information
      return {
        ...updatedCustomer,
        customFields: {
          customerBranchId: customFields?.fields?.customerBranch ?? null,
          customerClassification: customFields?.fields?.customerClassification ?? null,
          tourNumber: customFields?.fields?.tourNumber ?? null,
          admSalesRepresentive: customFields?.fields?.admSalesRepresentive ?? null,
          paymentMethod: customFields?.fields?.paymentMethod ?? null,
          einzaCreditLimit: customFields?.fields?.einzaCreditLimit ?? null,
        },
        customCustomerCharacteristics,
        defaultBillingAddress: addresses.find((address) => address?.id === defaultBillingAddressId) ?? null,
      }
    } catch (e) {
      throw new Error(e)
    }
  },
}
